import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import {
    ClientPaymentApproverCandidate,
    ClientPaymentApproverCandidatesRequest,
} from '../../Core-Repositories/payment.repository';
import { ClientPaymentApproverPickerService, ClientPaymentApproverPickerState } from './clientPaymentApproverPicker.service';

export interface ClientPaymentApproverPickerModalParams {
    paymentIds?: number[];
    taskIds?: number[];
}

export interface ClientPaymentApproverPickerSelectionResult {
    contactId: number;
    displayName: string;
}

@Component({
    selector: 'client-payment-approver-picker-modal',
    templateUrl: './clientPaymentApproverPickerModal.component.html',
    standalone: false,
})
export class ClientPaymentApproverPickerModalComponent implements OnInit {
    constructor(
        private readonly pickerService: ClientPaymentApproverPickerService,
        private readonly bsModalRef: BsModalRef,
    ) {}

    params: ClientPaymentApproverPickerModalParams;
    result: ClientPaymentApproverPickerSelectionResult;

    includeAllCompanyContacts = false;
    autoExpandedToAllContacts = false;
    loading = false;
    candidates: ClientPaymentApproverCandidate[] = [];
    selectedContactId: number = null;
    selectedDisplayName: string = null;

    async ngOnInit(): Promise<void> {
        this.loading = true;
        try {
            this.applyState(await this.pickerService.loadInitial(this.getRequest()));
        } finally {
            this.loading = false;
        }
    }

    async onShowAllCompanyContactsChanged(): Promise<void> {
        this.autoExpandedToAllContacts = false;
        this.pickerService.saveShowAllPreference(this.includeAllCompanyContacts);
        await this.loadCandidates();
    }

    get isSelectionValid(): boolean {
        if (!this.selectedContactId || !this.selectedDisplayName) {
            return false;
        }
        const candidate = this.candidates.find((x) => x.contactId === this.selectedContactId);
        return candidate != null && candidate.displayName === this.selectedDisplayName;
    }

    onApproverSelected(event: TypeaheadMatch): void {
        const candidate = event.item as ClientPaymentApproverCandidate;
        this.selectedContactId = candidate.contactId;
        this.selectedDisplayName = candidate.displayName;
    }

    save(): void {
        const selectedCandidate = this.candidates.find((x) => x.contactId === this.selectedContactId);
        if (!selectedCandidate) {
            return;
        }

        this.result = { contactId: selectedCandidate.contactId, displayName: selectedCandidate.displayName };
        this.cancel();
    }

    cancel(): void {
        this.bsModalRef.hide();
    }

    private async loadCandidates(): Promise<void> {
        this.loading = true;
        try {
            this.applyState(
                await this.pickerService.load(this.getRequest(), this.includeAllCompanyContacts, this.selectedContactId),
            );
        } finally {
            this.loading = false;
        }
    }

    private getRequest(): ClientPaymentApproverCandidatesRequest {
        return { paymentIds: this.params?.paymentIds ?? [], taskIds: this.params?.taskIds ?? [] };
    }

    private applyState(state: ClientPaymentApproverPickerState): void {
        this.candidates = state.candidates;
        this.includeAllCompanyContacts = state.includeAllCompanyContacts;
        this.autoExpandedToAllContacts = state.autoExpandedToAllContacts;
        this.selectedContactId = state.selectedContactId;
        if (!this.selectedContactId) {
            this.selectedDisplayName = null;
        }
    }
}
